import { useDynamicModalContext } from "@umami/components";
import { withTimeout } from "@umami/core";
import * as Auth from "@umami/social-auth";
import {
  accountsActions,
  useAppDispatch,
  useAsyncActionHandler,
  useCreateSocialWallets,
} from "@umami/state";
import { useCustomToast } from "@umami/utils";
import { minutesToMilliseconds } from "date-fns";
import { useCallback } from "react";

import { trackButtonClick } from "../../../utils/analytics";
import { setupPersistence } from "../../../utils/store";

const LOGIN_TIMEOUT = minutesToMilliseconds(1);

/**
 * Hook to onboard with a social identity provider.
 * Handles the authentication process and adds the account to the user's wallet.
 *
 * @param idp - the identity provider to use for authentication
 * @param onAuth - callback which will be called on a successful authentication
 */
export const useOnboardWithSocial = (idp: Auth.IDP, onAuth?: () => void) => {
  const toast = useCustomToast();
  const { onClose } = useDynamicModalContext();
  const { isLoading, handleAsyncAction } = useAsyncActionHandler();
  const createSocialWallets = useCreateSocialWallets();
  const dispatch = useAppDispatch();
  const onboard = useCallback(
    () =>
      handleAsyncAction(
        async () => {
          trackButtonClick(`continue_with_${idp}`, {
            button_label: idp,
            section: "Welcome",
            action: "button_click",
            flow: "Onboarding",
          });
          const { secretKey, name, id, email } = await withTimeout(
            () => Auth.forIDP(idp).getCredentials(),
            LOGIN_TIMEOUT
          );

          const account = await createSocialWallets({ secretKey, name, id, email, idp });

          // Initialize persistence with the secret key
          await setupPersistence(secretKey);

          dispatch(accountsActions.setCurrent(account.address.pkh));
          toast({ description: `Successfully added ${name || id} account`, status: "success" });
          onAuth?.();
          onClose();
        },
        { title: "Social login failed" }
      ),
    [idp, toast, handleAsyncAction, createSocialWallets, onAuth, onClose, dispatch]
  );

  return { isLoading, onboard };
};

/**
 * Hook to login with a social identity provider.
 * Handles the authentication process and decrypts the localstorage.
 *
 * @param idp - the identity provider to use for authentication
 */
export const useLoginWithSocial = (idp: Auth.IDP) => {
  const { isLoading, handleAsyncAction } = useAsyncActionHandler();

  const login = useCallback(
    () =>
      handleAsyncAction(
        async () => {
          const { secretKey } = await withTimeout(
            () => Auth.forIDP(idp).getCredentials(),
            LOGIN_TIMEOUT
          );
          await setupPersistence(secretKey);
        },
        { title: "Social login failed" }
      ),
    [idp, handleAsyncAction]
  );

  return { isLoading, login };
};
